/**
 * Gram strip — Store control photos only.
 * Autoplay + drag when the unique posts overflow; otherwise show them once, centered.
 */
(function () {
  const DRAG_THRESHOLD = 8;

  function bindImages(viewport) {
    viewport.querySelectorAll('img').forEach((img) => {
      if (img.dataset.igBound === 'true') return;
      img.dataset.igBound = 'true';
      img.loading = 'eager';
      img.draggable = false;
      const hideBroken = () => {
        if (img.classList.contains('adawat-ig__img')) {
          const placeholder = document.createElement('div');
          placeholder.className = 'adawat-ig__placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          img.replaceWith(placeholder);
          return;
        }
        img.remove();
      };
      img.addEventListener('error', hideBroken);
      if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) hideBroken();
    });
  }

  function init(root) {
    const viewport = root.querySelector('[data-ig-viewport]');
    const track = root.querySelector('[data-ig-track]');
    if (!viewport || !track || viewport.dataset.bound === 'true') return;
    viewport.dataset.bound = 'true';

    bindImages(viewport);

    const uniqueHTML = track.innerHTML;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = document.documentElement.getAttribute('dir') === 'rtl';
    let looping = false;
    let offset = 0;
    let loopWidth = 0;
    let hovering = false;
    let dragging = false;
    let dragMoved = false;
    let suppressClick = false;
    let startX = 0;
    let lastX = 0;
    let lastTs = 0;
    let pointerId = null;
    let rafId = 0;

    function uniqueWidth() {
      track.innerHTML = uniqueHTML;
      bindImages(viewport);
      return track.scrollWidth;
    }

    function enableLoop() {
      track.innerHTML = uniqueHTML + uniqueHTML;
      bindImages(viewport);
      looping = true;
      loopWidth = track.scrollWidth / 2;
      viewport.classList.remove('is-static');
    }

    function disableLoop() {
      track.innerHTML = uniqueHTML;
      bindImages(viewport);
      looping = false;
      loopWidth = 0;
      offset = 0;
      track.style.transform = '';
      viewport.classList.add('is-static');
    }

    function syncMode() {
      const width = uniqueWidth();
      const canLoop = !reduceMotion && width > viewport.clientWidth + 8;
      if (canLoop) enableLoop();
      else disableLoop();
      apply();
    }

    function apply() {
      if (!looping) {
        track.style.transform = '';
        return;
      }
      if (loopWidth > 0) {
        while (offset >= loopWidth) offset -= loopWidth;
        while (offset < 0) offset += loopWidth;
      }
      const x = rtl ? offset : -offset;
      track.style.transform = `translate3d(${x}px,0,0)`;
    }

    function durationSec() {
      const raw = getComputedStyle(root).getPropertyValue('--adawat-ig-speed');
      const n = parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 40;
    }

    function tick(ts) {
      if (!lastTs) lastTs = ts;
      const dt = Math.min(32, ts - lastTs);
      lastTs = ts;
      if (looping && !dragging && !hovering && loopWidth > 0) {
        offset += (loopWidth / durationSec()) * (dt / 1000);
        apply();
      }
      rafId = requestAnimationFrame(tick);
    }

    function onPointerDown(event) {
      if (!looping) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      dragging = true;
      dragMoved = false;
      startX = event.clientX;
      lastX = event.clientX;
      pointerId = event.pointerId;
      viewport.classList.add('is-dragging');
      try {
        viewport.setPointerCapture(event.pointerId);
      } catch (e) {
        /* ignore */
      }
    }

    function onPointerMove(event) {
      if (!looping || !dragging || (pointerId != null && event.pointerId !== pointerId)) return;
      const dx = event.clientX - lastX;
      lastX = event.clientX;
      if (!dragMoved && Math.abs(event.clientX - startX) >= DRAG_THRESHOLD) {
        dragMoved = true;
        suppressClick = true;
      }
      if (!dragMoved) return;
      event.preventDefault();
      offset += rtl ? dx : -dx;
      apply();
    }

    function onPointerUp(event) {
      if (!dragging || (pointerId != null && event.pointerId !== pointerId)) return;
      dragging = false;
      pointerId = null;
      viewport.classList.remove('is-dragging');
      lastTs = 0;
    }

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove, { passive: false });
    viewport.addEventListener('pointerup', onPointerUp);
    viewport.addEventListener('pointercancel', onPointerUp);
    viewport.addEventListener('lostpointercapture', onPointerUp);

    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      },
      true
    );

    viewport.addEventListener('mouseenter', () => {
      hovering = true;
    });
    viewport.addEventListener('mouseleave', () => {
      hovering = false;
      lastTs = 0;
    });

    document.addEventListener('visibilitychange', () => {
      lastTs = 0;
    });

    window.addEventListener('resize', () => {
      syncMode();
    });

    syncMode();
    rafId = requestAnimationFrame(tick);
    root._adawatIgStop = () => cancelAnimationFrame(rafId);
  }

  function initAll() {
    document.querySelectorAll('[data-adawat-instagram]').forEach((root) => {
      if (window.AdawatLazyInit?.whenVisible) {
        window.AdawatLazyInit.whenVisible(root, () => init(root));
      } else {
        init(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target?.querySelector?.('[data-adawat-instagram]') || e.target;
    if (root?.matches?.('[data-adawat-instagram]')) {
      if (window.AdawatLazyInit?.whenVisible) {
        window.AdawatLazyInit.whenVisible(root, () => init(root));
      } else {
        init(root);
      }
    }
  });
})();
